'use strict'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  CircularProgress,
  Collapse
} from '@material-ui/core'
import PropertyRowDetail from './PropertyRowDetail'
import { getCrewColor, getRouteColor } from './crewColors'
import { sortDays, buildFrequencyLabel } from './scheduleUtils'
import { useTerritory } from './TerritoryContext'
import { DFFilterChip, DFSearchInput, DFTag } from '../../df'
import { Link } from 'react-router-dom'

const formatMinutes = (minutes) => {
  const rounded = Math.round(Number(minutes || 0))
  if (!rounded) return '-'
  const hours = Math.floor(rounded / 60)
  const mins = rounded % 60
  if (!hours) return `${mins}m`
  if (!mins) return `${hours}h`
  return `${hours}h ${mins}m`
}

const getEstimatedMinutes = (row) => {
  const overrideMinutes = Number(row.estimatedMinutesOnSite || 0)
  if (overrideMinutes > 0) return overrideMinutes
  const estimatedHours = Number(row.estimatedHours || 0)
  return estimatedHours > 0 ? estimatedHours * 60 : 0
}

const getAvgOnSiteMinutes = (row) => {
  const avgMinutes = Number(row.avgOnSiteMinutes || 0)
  if (avgMinutes > 0) return avgMinutes
  const avgHours = Number(row.avgOnSiteHours || 0)
  return avgHours > 0 ? avgHours * 60 : 0
}

/* ── Inline chevron SVG ─────────────────────────────────────────── */
const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)
const RefreshSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)

export default function PropertiesTab ({
  url,
  selectedScenarioId,
  isScenarioEditable,
  crews,
  crewIds,
  routes,
  routeIds,
  onSnackbar,
  onRefreshMetrics,
  onRefreshDiff,
  onCountsChange
}) {
  const { scenarioMetrics, loadingMetrics, fetchMetrics, setPropertyCounts } = useTerritory()

  const [rows, setRows] = useState([])
  const [originalRowsById, setOriginalRowsById] = useState({})
  const [loading, setLoading] = useState(true)
  const [expandedRowId, setExpandedRowId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [onlyVip, setOnlyVip] = useState(false)
  const [onlyLocked, setOnlyLocked] = useState(false)
  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false)
  const [onlyUnassigned, setOnlyUnassigned] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [savingBySiteId, setSavingBySiteId] = useState({})
  const [movingBySiteId, setMovingBySiteId] = useState({})
  const [moveTargetBySiteId, setMoveTargetBySiteId] = useState({})
  const [suggestingBySiteId, setSuggestingBySiteId] = useState({})
  const [bulkSuggesting, setBulkSuggesting] = useState(false)
  const [resyncingBySiteId, setResyncingBySiteId] = useState({})
  const [metricsExpanded, setMetricsExpanded] = useState(false)

  const crewNameById = useMemo(() => {
    const map = {}
    crews.forEach(crew => { map[crew._id] = crew.name })
    return map
  }, [crews])

  const routeNameById = useMemo(() => {
    const map = {}
    ;(routes || []).forEach(r => { map[r._id] = r.name })
    return map
  }, [routes])

  const fetchData = useCallback(async (scenarioId) => {
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/properties`, {
        params: scenarioId ? { scenarioId } : undefined
      })
      const payload = response.data || {}
      const nextRows = payload.properties || []
      const nextOriginalRowsById = {}
      nextRows.forEach(row => {
        nextOriginalRowsById[row.aspirePropertyId] = {
          lockedCrewId: row.lockedCrewId || '',
          isVip: !!row.isVip,
          frequency: row.frequency || '',
          preferredDay: row.preferredDay || '',
          preferredDays: sortDays(row.preferredDays || []),
          weekType: row.weekType || ''
        }
      })
      setRows(nextRows)
      setOriginalRowsById(nextOriginalRowsById)
      setMoveTargetBySiteId(nextRows.reduce((acc, row) => {
        acc[row.aspirePropertyId] = row.assignedRouteId || row.assignedCrewId || ''
        return acc
      }, {}))
    } catch (err) {
      console.error('Error loading territory properties:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to load properties', 'error')
    } finally {
      setLoading(false)
    }
  }, [url, onSnackbar])

  useEffect(() => {
    fetchData(selectedScenarioId || null)
  }, [selectedScenarioId, fetchData])

  useEffect(() => {
    const counts = {
      total: rows.length,
      vip: rows.filter(r => r.isVip).length,
      locked: rows.filter(r => !!r.lockedCrewId).length,
      unassigned: rows.filter(r => !r.assignedRouteId && !r.assignedCrewId).length,
      anchors: rows.filter(r => r.anchor && r.anchor.isAnchor).length
    }
    setPropertyCounts(counts)
    if (onCountsChange) onCountsChange(counts)
  }, [rows, setPropertyCounts, onCountsChange])

  const isRowDirty = useCallback((row) => {
    const original = originalRowsById[row.aspirePropertyId]
    if (!original) return false
    const rowDays = sortDays(row.preferredDays || [])
    const origDays = sortDays(original.preferredDays || [])
    return (row.lockedCrewId || '') !== (original.lockedCrewId || '') ||
      !!row.isVip !== !!original.isVip ||
      (row.frequency || '') !== (original.frequency || '') ||
      (row.preferredDay || '') !== (original.preferredDay || '') ||
      (row.weekType || '') !== (original.weekType || '') ||
      rowDays.join('|') !== origDays.join('|')
  }, [originalRowsById])

  const updateRowField = (aspirePropertyId, field, value) => {
    setRows(prev => prev.map(row => {
      if (row.aspirePropertyId !== aspirePropertyId) return row
      return { ...row, [field]: value }
    }))
  }

  const updateLegacyFrequency = (aspirePropertyId, nextFrequency) => {
    setRows(prev => prev.map(row => {
      if (row.aspirePropertyId !== aspirePropertyId) return row
      const updated = { ...row, frequency: nextFrequency || '' }
      if (nextFrequency === 'Every Other Day (M-W-F)') {
        updated.preferredDays = ['Monday', 'Wednesday', 'Friday']
        updated.preferredDay = 'Monday'
        updated.weekType = ''
      } else if (nextFrequency === 'Twice a Week') {
        if (!Array.isArray(updated.preferredDays) || !updated.preferredDays.length) {
          updated.preferredDays = updated.preferredDay ? [updated.preferredDay] : ['Monday', 'Thursday']
        }
        updated.weekType = ''
      } else if (nextFrequency === 'Weekly') {
        updated.preferredDay = updated.preferredDay || 'Monday'
        updated.preferredDays = []
        updated.weekType = ''
      } else if (nextFrequency === 'Bi-Weekly') {
        updated.preferredDay = updated.preferredDay || 'Monday'
        updated.preferredDays = []
      } else {
        updated.weekType = ''
      }
      return updated
    }))
  }

  const isLiveMode = !selectedScenarioId

  const saveRow = async (row) => {
    if (!isRowDirty(row)) return
    setSavingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: true }))
    try {
      const params = selectedScenarioId ? { scenarioId: selectedScenarioId } : {}
      const response = await axios.patch(
        `${url}/api/hmi/territory/properties/${row.aspirePropertyId}`,
        {
          contractId: row.contractId,
          lockedCrewId: row.lockedCrewId || null,
          isVip: !!row.isVip,
          frequency: row.frequency || null,
          preferredDay: row.preferredDay || null,
          preferredDays: sortDays(row.preferredDays || []),
          weekType: row.weekType || null
        },
        { params }
      )
      const updated = response.data?.property
      if (updated) {
        setRows(prev => prev.map(item => item.aspirePropertyId === row.aspirePropertyId ? { ...item, ...updated } : item))
        setOriginalRowsById(prev => ({
          ...prev,
          [row.aspirePropertyId]: {
            lockedCrewId: updated.lockedCrewId || '',
            isVip: !!updated.isVip,
            frequency: updated.frequency || '',
            preferredDay: updated.preferredDay || '',
            preferredDays: sortDays(updated.preferredDays || []),
            weekType: updated.weekType || ''
          }
        }))
      } else {
        setOriginalRowsById(prev => ({
          ...prev,
          [row.aspirePropertyId]: {
            lockedCrewId: row.lockedCrewId || '',
            isVip: !!row.isVip,
            frequency: row.frequency || '',
            preferredDay: row.preferredDay || '',
            preferredDays: sortDays(row.preferredDays || []),
            weekType: row.weekType || ''
          }
        }))
      }
      onSnackbar(isLiveMode ? 'Live override saved' : 'Draft updated', 'success')
      fetchMetrics(selectedScenarioId || null)
      if (onRefreshMetrics) onRefreshMetrics()
      if (onRefreshDiff) onRefreshDiff()
    } catch (err) {
      console.error('Error updating territory property:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to update property', 'error')
    } finally {
      setSavingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: false }))
    }
  }

  const moveRow = async (row) => {
    if (!selectedScenarioId || !isScenarioEditable) return
    const moveTarget = moveTargetBySiteId[row.aspirePropertyId] || ''
    if (!moveTarget) return

    // Determine if moveTarget is a routeId or crewId
    const isRouteTarget = (routes || []).some(r => r._id === moveTarget)
    const currentId = row.assignedRouteId || row.assignedCrewId || ''
    if (moveTarget === currentId) return

    setMovingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: true }))
    try {
      const body = { aspirePropertyId: row.aspirePropertyId }
      if (isRouteTarget) {
        body.toRouteId = moveTarget
      } else {
        body.toCrewId = moveTarget
      }
      const response = await axios.post(
        `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`,
        body
      )
      const updatedAssignment = response.data?.assignment || null
      setRows(prev => prev.map(item => {
        if (item.aspirePropertyId !== row.aspirePropertyId) return item
        const assignedCrewId = updatedAssignment?.assignedCrewId || null
        const assignedRouteId = updatedAssignment?.assignedRouteId || null
        const routeName = assignedRouteId ? (routeNameById[assignedRouteId] || null) : null
        return {
          ...item,
          assignedCrewId,
          assignedRouteId,
          assignedCrewName: crewNameById[assignedCrewId] || 'Unassigned',
          assignedRouteName: routeName
        }
      }))
      setMoveTargetBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: moveTarget }))
      const targetName = isRouteTarget
        ? (routeNameById[moveTarget] || 'selected route')
        : (crewNameById[moveTarget] || 'selected crew')
      onSnackbar(`Moved ${row.propertyName || `Aspire #${row.aspirePropertyId}`} to ${targetName}`, 'success')
      fetchMetrics(selectedScenarioId)
      if (onRefreshMetrics) onRefreshMetrics()
      if (onRefreshDiff) onRefreshDiff()
    } catch (err) {
      console.error('Error moving scenario property:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to move property', 'error')
    } finally {
      setMovingBySiteId(prev => ({ ...prev, [row.aspirePropertyId]: false }))
    }
  }

  const suggestNearestForRow = async (row) => {
    if (!selectedScenarioId || !row?.aspirePropertyId) return
    const propId = row.aspirePropertyId
    setSuggestingBySiteId(prev => ({ ...prev, [propId]: true }))
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/suggest-nearest`, {
        aspirePropertyIds: [propId],
        includeAssigned: true,
        limit: 1
      })
      const suggestion = response.data?.suggestions?.[0]
      if (!suggestion?.suggestedCrewId && !suggestion?.suggestedRouteId) {
        onSnackbar('No suggestion available for this property', 'info')
        return
      }
      // Prefer route-based suggestion
      const suggestedTarget = suggestion.suggestedRouteId || suggestion.suggestedCrewId
      setMoveTargetBySiteId(prev => ({ ...prev, [propId]: suggestedTarget }))
      const hasDriveMin = Number.isFinite(Number(suggestion.driveMinutes))
      const metricLabel = hasDriveMin ? `${Math.round(Number(suggestion.driveMinutes))} min` : `${suggestion.distanceMiles || 0} mi`
      const methodLabel = suggestion.selectionMethod === 'ors_matrix' ? 'ORS' : 'Haversine'
      onSnackbar(
        `Suggested ${suggestion.suggestedCrewName || 'crew'} (${metricLabel}, ${methodLabel})`,
        suggestion.isAmbiguous ? 'warning' : 'success'
      )
    } catch (err) {
      console.error('Error getting nearest-crew suggestion:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to suggest nearest crew', 'error')
    } finally {
      setSuggestingBySiteId(prev => ({ ...prev, [propId]: false }))
    }
  }

  const bulkSuggestUnassigned = async () => {
    if (!selectedScenarioId || !isScenarioEditable) return
    const unassigned = rows.filter(r => !r.assignedRouteId && !r.assignedCrewId)
    if (!unassigned.length) {
      onSnackbar('All properties are already assigned', 'info')
      return
    }
    setBulkSuggesting(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/suggest-nearest`, {
        aspirePropertyIds: unassigned.map(r => r.aspirePropertyId),
        includeAssigned: false,
        limit: 200
      })
      const suggestions = response.data?.suggestions || []
      if (!suggestions.length) {
        onSnackbar('No suggestions available for unassigned properties', 'info')
        return
      }
      const nextTargets = {}
      suggestions.forEach(s => {
        const target = s.suggestedRouteId || s.suggestedCrewId
        if (target) {
          nextTargets[s.aspirePropertyId] = target
        }
      })
      setMoveTargetBySiteId(prev => ({ ...prev, ...nextTargets }))
      onSnackbar(`Suggested crews for ${Object.keys(nextTargets).length} of ${unassigned.length} unassigned properties`, 'success')
    } catch (err) {
      console.error('Error bulk suggesting:', err)
      onSnackbar(err.response?.data?.message || err.message || 'Failed to bulk suggest', 'error')
    } finally {
      setBulkSuggesting(false)
    }
  }

  const resyncProperty = async (row) => {
    const propId = row.aspirePropertyId
    if (!propId) return
    setResyncingBySiteId(prev => ({ ...prev, [propId]: true }))
    try {
      const response = await axios.post(`${url}/api/aspire/routing/resync-property`, {
        propertyId: propId
      })
      if (response.data?.success) {
        onSnackbar(`Re-synced ${row.propertyName || `#${propId}`} from Aspire`, 'success')
        // Refresh the property list to pick up updated data
        fetchData(selectedScenarioId || null)
        fetchMetrics(selectedScenarioId || null)
      } else {
        onSnackbar(response.data?.error || 'Resync failed', 'error')
      }
    } catch (err) {
      console.error('Error resyncing property:', err)
      onSnackbar(err.response?.data?.error || err.message || 'Failed to resync property', 'error')
    } finally {
      setResyncingBySiteId(prev => ({ ...prev, [propId]: false }))
    }
  }

  const filteredRows = useMemo(() => {
    let next = [...rows]
    if (searchText.trim()) {
      const search = searchText.trim().toLowerCase()
      next = next.filter(row => {
        const haystack = [row.propertyName, row.clientName, row.address, row.city, row.customerType, row.frequency, row.assignedRouteName, row.assignedCrewName, row.lockedCrewName]
          .filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(search)
      })
    }
    if (onlyVip) next = next.filter(row => row.isVip)
    if (onlyLocked) next = next.filter(row => !!row.lockedCrewId)
    if (onlyNeedsReview) next = next.filter(row => row.frequencyConfidence === 'needs_review' || row.frequencyCode === 'UNKNOWN')
    if (onlyUnassigned) next = next.filter(row => !row.assignedRouteId && !row.assignedCrewId)
    return next
  }, [rows, searchText, onlyVip, onlyLocked, onlyNeedsReview, onlyUnassigned])

  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])

  const fleet = scenarioMetrics?.fleet
  const unassignedCount = rows.filter(r => !r.assignedRouteId && !r.assignedCrewId).length
  const vipCount = rows.filter(r => r.isVip).length
  const lockedCount = rows.filter(r => !!r.lockedCrewId).length
  const anchorCount = rows.filter(r => r.anchor && r.anchor.isAnchor).length

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <CircularProgress size={28} style={{ color: 'var(--df-primary)' }} />
      </div>
    )
  }

  const routeMetrics = Array.isArray(scenarioMetrics?.routes) ? scenarioMetrics.routes.filter(r => r.routeId !== 'unassigned') : []
  const totalSvcMin = fleet?.totalServiceMinutes || 0
  const totalOtMin = fleet?.totalOTMinutes || 0
  const pageStart = page * rowsPerPage
  const pageEnd = Math.min(pageStart + rowsPerPage, filteredRows.length)

  return (
    <div className="content-pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Metrics strip ──────────────────────────────────────────── */}
      {fleet && (
        <div className="rw-card" style={{ padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            {loadingMetrics && <CircularProgress size={14} style={{ color: 'var(--df-primary)' }} />}
            {isLiveMode && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99,
                fontSize: 10, fontWeight: 700, background: 'var(--df-accent, #c66a45)', color: '#fff'
              }}>LIVE</span>
            )}
            <MetricCell label="Routes" value={routeMetrics.length || fleet.crewCount || 0} />
            <div style={{ width: 1, height: 32, background: 'var(--df-line)' }} />
            <MetricCell label="Properties" value={fleet.totalProperties || 0} />
            <MetricCell label="VIP" value={fleet.totalVip || vipCount} />
            <MetricCell label="Unassigned" value={unassignedCount} warn={unassignedCount > 0} />
            <MetricCell label="Locked" value={fleet.totalLockedCount || lockedCount} />
            <MetricCell label="Anchors" value={fleet.totalAnchorCount || anchorCount} />
            <div style={{ width: 1, height: 32, background: 'var(--df-line)' }} />
            <MetricCell label="Svc / week" value={formatMinutes(totalSvcMin)} />
            <MetricCell label="OT / week" value={formatMinutes(totalOtMin)} warn={totalOtMin > 0} />
            {routeMetrics.length > 0 && (
              <button
                onClick={() => setMetricsExpanded(o => !o)}
                style={{
                  marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5,
                  fontSize: 11, fontWeight: 600, color: 'var(--df-primary)', background: 'none',
                  border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 5
                }}
              >
                {metricsExpanded ? 'Hide route breakdown' : 'Route breakdown'}
                <span style={{ transition: 'transform 0.2s', transform: metricsExpanded ? 'rotate(180deg)' : 'none', display: 'flex' }}>
                  <ChevronDown />
                </span>
              </button>
            )}
          </div>

          {/* Route breakdown table */}
          {metricsExpanded && routeMetrics.length > 0 && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--df-line)', paddingTop: 14, overflowX: 'auto' }}>
              <table className="tbl" style={{ fontSize: 12 }}>
                <thead>
                  <tr>
                    <th>Route</th>
                    <th>Props</th>
                    <th>VIP</th>
                    <th>Service</th>
                    <th>Actual</th>
                    <th>Drive</th>
                    <th>Miles</th>
                    <th>Total</th>
                    <th>OT</th>
                  </tr>
                </thead>
                <tbody>
                  {routeMetrics
                    .sort((a, b) => Number(b.weeklyTotalMinutes || 0) - Number(a.weeklyTotalMinutes || 0))
                    .map(item => (
                      <tr key={item.routeId}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{
                              display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                              backgroundColor: getRouteColor(item.routeId, routeIds || [])
                            }} />
                            <span style={{ fontWeight: 600 }}>{item.routeName}</span>
                          </div>
                        </td>
                        <td>{item.propertyCount || 0}</td>
                        <td>{item.vipCount || 0}</td>
                        <td>{formatMinutes(item.weeklyServiceMinutes)}</td>
                        <td style={{ color: (item.actualWeeklyServiceMinutes || 0) !== (item.weeklyServiceMinutes || 0) ? 'var(--df-warn-text, #8c5b00)' : undefined }}>
                          {formatMinutes(item.actualWeeklyServiceMinutes)}
                        </td>
                        <td>{formatMinutes(item.estimatedWeeklyDriveMinutes)}</td>
                        <td>{item.estimatedWeeklyDriveMiles || 0}</td>
                        <td style={{ fontWeight: 600 }}>{formatMinutes(item.weeklyTotalMinutes)}</td>
                        <td style={{
                          color: (item.estimatedWeeklyOTMinutes || 0) > 0 ? 'var(--df-danger, #a9341d)' : 'var(--df-ink-4)',
                          fontWeight: (item.estimatedWeeklyOTMinutes || 0) > 0 ? 700 : 400
                        }}>
                          {(item.estimatedWeeklyOTMinutes || 0) > 0 ? formatMinutes(item.estimatedWeeklyOTMinutes) : '—'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <DFSearchInput
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setPage(0) }}
          placeholder="Search properties…"
        />
        <DFFilterChip label="All" active={!onlyVip && !onlyLocked && !onlyUnassigned && !onlyNeedsReview} onClick={() => { setOnlyVip(false); setOnlyLocked(false); setOnlyUnassigned(false); setOnlyNeedsReview(false); setPage(0) }} />
        <DFFilterChip label="VIP" count={vipCount} active={onlyVip} onClick={() => { setOnlyVip(v => !v); setPage(0) }} />
        <DFFilterChip label="Locked" count={lockedCount} active={onlyLocked} onClick={() => { setOnlyLocked(v => !v); setPage(0) }} />
        <DFFilterChip
          label="Unassigned"
          count={unassignedCount}
          active={onlyUnassigned}
          warn={!onlyUnassigned && unassignedCount > 0}
          onClick={() => { setOnlyUnassigned(v => !v); setPage(0) }}
        />
        <DFFilterChip label="Needs Review" active={onlyNeedsReview} onClick={() => { setOnlyNeedsReview(v => !v); setPage(0) }} />

        {onlyUnassigned && (
          <button
            onClick={bulkSuggestUnassigned}
            disabled={!isScenarioEditable || bulkSuggesting}
            style={{
              padding: '5px 12px', borderRadius: 6, border: '1px solid var(--df-line-2)',
              background: 'var(--df-surface)', color: 'var(--df-primary)', fontSize: 12, fontWeight: 600,
              cursor: (!isScenarioEditable || bulkSuggesting) ? 'not-allowed' : 'pointer',
              opacity: (!isScenarioEditable || bulkSuggesting) ? 0.5 : 1, fontFamily: 'inherit'
            }}
          >
            {bulkSuggesting ? 'Suggesting…' : 'Suggest Routes'}
          </button>
        )}

        <button
          onClick={() => { fetchData(selectedScenarioId || null); fetchMetrics(selectedScenarioId || null) }}
          title="Refresh"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
            borderRadius: 6, border: '1px solid var(--df-line-2)', background: 'var(--df-surface)',
            color: 'var(--df-ink-3)', cursor: 'pointer', flexShrink: 0
          }}
        >
          <RefreshSvg />
        </button>

        <span style={{ fontSize: 12, color: 'var(--df-ink-4)', marginLeft: 'auto' }}>
          {filteredRows.length} of {rows.length}
        </span>
      </div>

      {/* ── Property Table ─────────────────────────────────────────── */}
      <div className="rw-card" style={{ overflow: 'hidden' }}>
        <div className="tbl-wrap" style={{ maxHeight: 'calc(100vh - 340px)' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 260 }}>Property</th>
                <th style={{ width: 90 }}>Source</th>
                <th style={{ width: 160 }}>Frequency</th>
                <th style={{ width: 70, textAlign: 'right' }}>Est</th>
                <th style={{ width: 95, textAlign: 'right' }}>Avg On-Site</th>
                <th style={{ width: 130 }}>Route</th>
                <th style={{ width: 80 }}>Flags</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map(row => {
                const isExpanded = expandedRowId === row.aspirePropertyId
                const dirty = isRowDirty(row)
                const crewColor = row.assignedRouteId
                  ? getRouteColor(row.assignedRouteId, routeIds || [])
                  : getCrewColor(row.assignedCrewId, crewIds)
                const estimatedMinutes = getEstimatedMinutes(row)
                const avgOnSiteMinutes = getAvgOnSiteMinutes(row)
                const isUnassigned = !row.assignedRouteId && !row.assignedCrewId

                const linkStatus = row.sourceStatus || row.linkStatus
                let sourceLabel = 'Aspire'
                if (linkStatus === 'agreement_linked') sourceLabel = 'Legacy'
                else if (linkStatus === 'jobsite_only') sourceLabel = 'Jobsite'

                let sourceColor = 'var(--df-ink-4)'
                if (linkStatus === 'agreement_linked') sourceColor = 'var(--df-primary)'

                return (
                  <React.Fragment key={row.aspirePropertyId}>
                    <tr
                      className={isExpanded ? 'selected' : ''}
                      onClick={() => setExpandedRowId(isExpanded ? null : row.aspirePropertyId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ maxWidth: 260 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--df-ink)' }}>{row.propertyName || '-'}</div>
                        <div style={{ fontSize: 11, color: 'var(--df-ink-4)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <Link
                            style={{ color: '#769ba2', textDecoration: 'none' }}
                            target="_blank"
                            to={{ pathname: `https://cloud.youraspire.com/app/properties/overview/${row.aspirePropertyId}` }}
                          >
                            {[row.address, row.city, `#${row.aspirePropertyId}`].filter(Boolean).join(' · ')}
                          </Link>
                        </div>
                      </td>
                      <td>
                        <DFTag label={sourceLabel} color={sourceColor} />
                      </td>
                      <td>
                        <div style={{ fontSize: 12, color: 'var(--df-ink-2)' }}>{buildFrequencyLabel(row)}</div>
                        {(row.frequencyConfidence === 'needs_review' || row.frequencyCode === 'UNKNOWN') && (
                          <DFTag label="Needs review" color="#c66a45" />
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 12, color: 'var(--df-ink-2)' }}>{formatMinutes(estimatedMinutes)}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 12, color: 'var(--df-ink-2)' }}>{formatMinutes(avgOnSiteMinutes)}</span>
                      </td>
                      <td>
                        {isUnassigned ? (
                          <span style={{ fontSize: 12, color: 'var(--df-danger, #a9341d)', fontWeight: 600 }}>Unassigned</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                              backgroundColor: crewColor
                            }} />
                            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--df-ink-2)' }}>
                              {row.assignedRouteName || row.assignedCrewName || 'Unassigned'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {row.isVip && <DFTag label="VIP" color="#a4502f" />}
                          {!!row.lockedCrewId && <DFTag label="Locked" color="#3454c1" />}
                          {(row.sourceStatus === 'unmapped' || row.linkStatus === 'unmapped') && <DFTag label="Unmapped" color="#6e3fb7" />}
                        </div>
                      </td>
                      <td>
                        <div style={{
                          display: 'flex', color: 'var(--df-ink-4)', transition: 'transform 0.15s',
                          transform: isExpanded ? 'rotate(180deg)' : 'none'
                        }}>
                          <ChevronDown />
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <PropertyRowDetail
                              row={row}
                              crews={crews}
                              routes={routes}
                              isEditable={isScenarioEditable || isLiveMode}
                              isLiveMode={isLiveMode}
                              isDirty={dirty}
                              isSaving={!!savingBySiteId[row.aspirePropertyId]}
                              isMoving={!!movingBySiteId[row.aspirePropertyId]}
                              isSuggesting={!!suggestingBySiteId[row.aspirePropertyId]}
                              isResyncing={!!resyncingBySiteId[row.aspirePropertyId]}
                              moveTarget={moveTargetBySiteId[row.aspirePropertyId] || row.assignedCrewId || ''}
                              onUpdateField={updateRowField}
                              onUpdateFrequency={updateLegacyFrequency}
                              onMoveTargetChange={(id, val) => setMoveTargetBySiteId(prev => ({ ...prev, [id]: val }))}
                              onMove={moveRow}
                              onSuggest={suggestNearestForRow}
                              onSave={saveRow}
                              onResync={resyncProperty}
                              onCollapse={() => setExpandedRowId(null)}
                            />
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
              {paginatedRows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center' }}>
                    <div style={{ padding: 24, color: 'var(--df-ink-4)', fontSize: 13 }}>No properties match your filters</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ─────────────────────────────────────────── */}
        <div style={{
          padding: '10px 14px', borderTop: '1px solid var(--df-line)',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10
        }}>
          <span style={{ fontSize: 12, color: 'var(--df-ink-4)' }}>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            style={{
              padding: '3px 6px', borderRadius: 4, border: '1px solid var(--df-line-2)',
              background: 'var(--df-surface)', fontSize: 12, fontFamily: 'inherit',
              color: 'var(--df-ink-2)', cursor: 'pointer'
            }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--df-ink-4)' }}>
            {pageStart + 1}–{pageEnd} of {filteredRows.length}
          </span>
          <button
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            style={{
              border: '1px solid var(--df-line-2)', background: 'var(--df-surface)', borderRadius: 4,
              padding: '3px 8px', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.4 : 1, fontFamily: 'inherit', fontSize: 12
            }}
          >&lsaquo;</button>
          <button
            disabled={pageEnd >= filteredRows.length}
            onClick={() => setPage(p => p + 1)}
            style={{
              border: '1px solid var(--df-line-2)', background: 'var(--df-surface)', borderRadius: 4,
              padding: '3px 8px', cursor: pageEnd >= filteredRows.length ? 'not-allowed' : 'pointer', opacity: pageEnd >= filteredRows.length ? 0.4 : 1, fontFamily: 'inherit', fontSize: 12
            }}
          >&rsaquo;</button>
        </div>
      </div>
    </div>
  )
}

/* ── MetricCell helper ───────────────────────────────────────────── */
function MetricCell ({ label, value, warn }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 64 }}>
      <div style={{
        fontSize: 18, fontWeight: 700, lineHeight: 1.2,
        color: warn ? 'var(--df-danger, #a9341d)' : 'var(--df-ink)'
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 10, fontWeight: 600, color: 'var(--df-ink-4)',
        textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 1
      }}>
        {label}
      </div>
    </div>
  )
}
